/**
 * <dw-reviews>: sorting, star filtering and "show more" for the reviews section.
 *
 * The first page is rendered by Liquid. Further pages come from /products/<handle>?view=reviews&page=n
 * (templates/product.reviews.liquid) and are fetched on demand. Sorting by anything but the server
 * order, and star filters, need every review, so they load the remaining pages first.
 */
// A page can have the script twice (a bundle page has the reviews of each child): everything is inside this block, so a second copy declares nothing at the top level of the
// page (a repeated top-level const or class is a SyntaxError that the page logs as an uncaught error) and defines nothing twice.
if (!customElements.get('dw-reviews')) {
// Country names in the visitor's language from the ISO code, the server renders the English name as a fallback
const regionNames = (() => {
  try { return new Intl.DisplayNames([document.documentElement.lang || 'en'], { type: 'region' }); } catch (e) { return null; }
})();

const localizeCountries = (root) => {
  if (!regionNames) return;
  root.querySelectorAll('[data-country-code]').forEach((el) => {
    const code = el.dataset.countryCode;
    if (!code) return;
    try { el.textContent = regionNames.of(code) || el.textContent; } catch (e) { /* keep the English name */ }
  });
};

// Short and natural, in the visitor's language: "7 mån", "3 v", "2 d", "5 tim" (not "för 7 månader sedan"). The browser's own short unit
// names are used (Intl.NumberFormat), so every language gets its usual abbreviation. Under a minute it is the word for "now". The <time>
// keeps its datetime attribute, and the absolute date the server rendered becomes the tooltip.
const locale = document.documentElement.lang || 'en';

const now = (() => {
  try { return new Intl.RelativeTimeFormat([locale], { numeric: 'auto' }).format(0, 'second'); } catch (e) { return null; }
})();

const unitFormats = new Map();
const short = (value, unit) => {
  try {
    if (!unitFormats.has(unit)) unitFormats.set(unit, new Intl.NumberFormat([locale], { style: 'unit', unit, unitDisplay: 'short' }));
    return unitFormats.get(unit).format(value);
  } catch (e) {
    return null;
  }
};

const UNITS = [['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60]];

const relativize = (root) => {
  root.querySelectorAll('time[datetime]').forEach((el) => {
    const then = Date.parse(el.getAttribute('datetime'));
    if (Number.isNaN(then)) return;
    const seconds = Math.max(0, (Date.now() - then) / 1000);
    const [unit, size] = UNITS.find(([, length]) => seconds >= length) || [null, 1];
    const text = unit ? short(Math.floor(seconds / size), unit) : now;
    if (!text) return; // no Intl support: the date the server rendered stays
    if (!el.title) el.title = el.textContent.trim();
    el.textContent = text;
  });
};

class DwReviews extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-list]');
    this.pageSize = parseInt(this.dataset.pageSize, 10) || 5;
    this.serverTotal = parseInt(this.dataset.total, 10) || 0;
    // the ratings of the product (more than the reviews of the list: the sync has not all of them)
    this.ratings = parseInt(this.dataset.ratings, 10) || this.serverTotal;
    this.endpoint = this.dataset.endpoint;

    this.items = Array.from(this.list.children);
    this.items.forEach((el, i) => { el.dataset.order = i; });
    this.ids = new Set(this.items.map((el) => el.dataset.id));
    this.nextPage = 1;
    this.exhausted = this.items.length >= this.serverTotal;
    this.loading = false;
    this.dirty = false;

    this.limit = this.items.length || this.pageSize;
    this.filter = null;
    this.sort = 'featured';

    this.result = this.querySelector('[data-result]');
    this.none = this.querySelector('[data-none]');
    this.more = this.querySelector('[data-more]');
    this.bars = Array.from(this.querySelectorAll('[data-filter]'));

    this.querySelector('[data-sort]').addEventListener('change', (e) => {
      this.sort = e.target.value;
      this.limit = this.pageSize;
      this.update();
    });
    this.bars.forEach((bar) => bar.addEventListener('click', () => {
      this.filter = bar.dataset.filter === this.filter ? null : bar.dataset.filter;
      this.limit = this.pageSize;
      this.update();
    }));
    this.querySelector('[data-reset]').addEventListener('click', () => {
      this.filter = null;
      this.update();
    });
    this.more?.addEventListener('click', () => {
      this.limit += this.pageSize;
      this.update();
    });

    localizeCountries(this.list);
    relativize(this.list);

    // "Read reply" / "Hide reply" in the byline. Delegated, so reviews fetched later work too.
    this.list.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-reply-toggle]');
      if (!toggle) return;
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(open));
      toggle.querySelector('[data-reply-label]').textContent = open ? toggle.dataset.hide : toggle.dataset.show;
      document.getElementById(toggle.getAttribute('aria-controls')).hidden = !open;
    });
    this.render();
  }

  // Every review is needed to sort or filter correctly, otherwise the "highest" ones might not be loaded yet.
  get needsAll() {
    return this.sort !== 'featured' || this.filter !== null;
  }

  async fetchPage() {
    const url = `${this.endpoint}&page=${this.nextPage}`;
    const html = await (await fetch(url)).text();
    const page = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-pages]');
    if (!page) throw new Error('No reviews in response');

    localizeCountries(page);
    relativize(page);
    Array.from(page.children).forEach((el) => {
      if (this.ids.has(el.dataset.id)) return;
      this.ids.add(el.dataset.id);
      el.dataset.order = this.items.length;
      el.hidden = true;
      this.items.push(el);
      this.list.appendChild(el);
    });

    this.exhausted = this.nextPage >= parseInt(page.dataset.pages, 10);
    this.nextPage += 1;
  }

  matches() {
    const num = (el, key) => Number(el.dataset[key]);
    const sorters = {
      featured: (a, b) => num(a, 'order') - num(b, 'order'),
      newest: (a, b) => num(b, 'date') - num(a, 'date'),
      oldest: (a, b) => num(a, 'date') - num(b, 'date'),
      highest: (a, b) => num(b, 'rating') - num(a, 'rating') || num(b, 'date') - num(a, 'date'),
      lowest: (a, b) => num(a, 'rating') - num(b, 'rating') || num(b, 'date') - num(a, 'date'),
    };
    return this.items
      .filter((el) => !this.filter || el.dataset.rating === this.filter)
      .sort(sorters[this.sort]);
  }

  // Loads whatever pages are missing, then renders. One load at a time; a change made while
  // loading is picked up by another pass once the current fetch settles.
  async update() {
    if (this.loading) {
      this.dirty = true;
      return;
    }

    const enough = () => this.exhausted
      || (!this.needsAll && this.matches().length > this.limit);

    this.loading = true;
    try {
      do {
        this.dirty = false;
        if (!enough()) this.setBusy(true);
        while (!enough()) await this.fetchPage();
      } while (this.dirty);
    } catch (e) {
      // Render what we have, the next click retries
    } finally {
      this.loading = false;
      this.setBusy(false);
    }
    this.render();
  }

  setBusy(busy) {
    this.setAttribute('aria-busy', String(busy));
    if (this.more) this.more.disabled = busy;
  }

  render() {
    const matches = this.matches();
    const visible = new Set(matches.slice(0, this.limit));

    // One reflow: re-append in display order, then toggle visibility
    const frag = document.createDocumentFragment();
    matches.forEach((el) => frag.appendChild(el));
    this.items.filter((el) => !visible.has(el) && !matches.includes(el)).forEach((el) => frag.appendChild(el));
    this.list.appendChild(frag);
    this.items.forEach((el) => { el.hidden = !visible.has(el); });

    const total = this.filter || this.exhausted ? matches.length : this.serverTotal;
    this.bars.forEach((bar) => bar.setAttribute('aria-pressed', String(bar.dataset.filter === this.filter)));
    this.none.hidden = matches.length > 0;
    if (this.more) this.more.parentElement.hidden = visible.size >= total;
    // what the toolbar says: all the ratings of the product, or the reviews with the chosen number of stars (a filter loads every page first, so that count is exact)
    const shown = this.filter ? matches.length : this.ratings;
    const category = new Intl.PluralRules(document.documentElement.lang || 'en').select(shown);
    const template = this.result.dataset[`template${category[0].toUpperCase()}${category.slice(1)}`] || this.result.dataset.templateOther;
    this.result.textContent = template.replace('[number]', shown);
  }
}

customElements.define('dw-reviews', DwReviews);
}
